import React, { Component, createRef } from 'react';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Button } from '@thd-olt-component-react/button';
import { Loader } from '@thd-olt-component-react/loader';
import { ExperienceContext, getDomPath } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { string, bool } from 'prop-types';
import helper from './helper';
import * as analytics from './analytics';
import './track-out-of-stock.style.scss';

const MESSAGE_STATES = {
  email_missing: {
    type: 'error',
    alertClass: 'danger',
    message: 'Please provide your email address, formatted as you@domain.com.',
    form: { enabled: true }
  },
  failure: {
    type: 'error',
    alertClass: 'danger',
    title: 'An error has occurred.',
    message: 'Please resubmit your email address.',
    form: { enabled: true }
  },
  duplicate: {
    type: 'success',
    alertClass: 'success',
    title: 'Thank you!',
    message: 'You are already signed up to receive a notification if this product is back in stock.',
    form: { enabled: false }
  },
  success: {
    type: 'success',
    alertClass: 'success',
    title: 'Thank you!',
    message: 'We\'ll send you an email if this product is back in stock.',
    form: { enabled: false }
  },
  init: {
    type: '',
    alertClass: '',
    title: '',
    message: '',
    form: { enabled: true }
  }
};

const INHOUSE_STATUS_MAP = {
  Success: 'success',
  OPS_ERROR_200: 'email_missing',
  OPS_ERROR_201: 'duplicate',
  OPS_ERROR_203: 'failure',
  OPS_ERROR_204: 'failure',
  OPS_ERROR_205: 'failure',
  OPS_DECRYPT_ERROR: 'failure',
};

const LMWrapper = ({ children, props }) => {
  const { internally } = useLayoutGroupProps({ props });
  return children({ internally });
};

class TrackOutOfStock extends Component {

  state = {
    email: (typeof window !== 'undefined' && window?.THDCustomer?.default?.email) || '',
    status: 'init',
    loader: false
  }

  trackError = (domPath) => {
    const { status } = this.state;
    const message = MESSAGE_STATES[status]?.message;
    analytics.track('click', { message, domPath });
  }

  handleNotifyMe = async (event, _path, internally) => {
    event.preventDefault();
    const { email } = this.state;
    this.setState({ loader: true });
    if (!helper.testEmail(helper.trimMe(email))) {
      this.setState({
        loader: false,
        status: 'email_missing'
      }, function () {
        this.trackError(_path);
      });

    } else {
      const { hosts, path } = this.context;
      const { itemId } = this.props;
      let notifyUrl = '';
      let notifyOptions = '';
      if (internally && itemId) {
        notifyUrl = '/discovery/opt/in';
        const payload = {
          emailId: email,
          omsId: itemId
        };
        notifyOptions = {
          method: 'POST',
          crossdomain: false,
          headers: {
            'Content-Type': 'application/json',
          },
          dataType: 'json',
          body: JSON.stringify(payload)
        };
      } else if (path) {
        const { trackif = '' } = hosts || {};
        notifyUrl = `${trackif}/tools/auto`;
        const itemURL = `${window.location.origin}${window.location.pathname}`;
        let formData = new FormData();
        formData.append('email', email);
        formData.append('url', typeof window !== 'undefined' ? itemURL : path);
        formData.append('preset', 'out-of-stock');
        notifyOptions = {
          method: 'POST',
          crossdomain: true,
          dataType: 'json',
          body: formData
        };
      }
      if (notifyUrl && notifyOptions) {
        try {
          let responseStatus = '';
          const notifyMeResponse = await fetch(notifyUrl, notifyOptions)
            .then((resp) => resp.json());
          analytics.track('click');
          if (internally) {
            responseStatus = INHOUSE_STATUS_MAP[notifyMeResponse.message];
          } else {
            responseStatus = notifyMeResponse.status;
          }
          this.setState({
            loader: false,
            status: (responseStatus || 'failure')
          });
        } catch (error) {
          this.setState({
            loader: false,
            status: 'failure'
          }, function () {
            this.trackError(_path);
          });
        }
      }
    }
  }

  updateEmailId = (event) => {
    const { value } = event.target;
    this.setState({ email: value });
  }

  render() {
    const { status, email, loader } = this.state;
    const ref = createRef();
    return (
      <LMWrapper props={this.props}>
        {({ internally }) => {
          return (
            <Row className="track-out-of-stock">
              <Col lg="12">
                <p>Receive an email if this item is back in stock.</p>
              </Col>
              <Col lg="12">
                {MESSAGE_STATES[status]?.form?.enabled && (
                  <div>
                    <form
                      className="u__clearfix track-out-of-stock__form"
                      onSubmit={(event) => {
                        const _path = getDomPath(ref);
                        this.handleNotifyMe(event, _path, internally);
                      }}
                    >
                      <input
                        type="email"
                        placeholder="Email"
                        className="form-input__field track-out-of-stock__email"
                        onChange={this.updateEmailId}
                        value={email}
                        data-qm-mask=""
                      />
                      <Button
                        ref={ref}
                        onClick={(event) => {
                          if (loader) {
                            event.preventDefault();
                          } else if (!loader) {
                            const _path = getDomPath(ref);
                            this.handleNotifyMe(event, _path, internally);
                          }
                        }}
                        className="track-out-of-stock__notifyme"
                      >
                        {loader && (
                          <Loader active bttn />
                        )}
                        {!loader && (
                          <span>Notify Me</span>
                        )}
                      </Button>
                    </form>
                  </div>
                )}
                {status
                && (
                  <div className="track-out-of-stock__status">
                    <div className={`alert-inline alert-inline--${MESSAGE_STATES[status].alertClass}`}>
                      <span className="alert-inline__message">
                        <span className="alert-inline__title">{MESSAGE_STATES[status].title} </span>
                        {MESSAGE_STATES[status].message}
                      </span>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          );
        }}
      </LMWrapper>
    );
  }
}

TrackOutOfStock.displayName = 'TrackOutOfStock';

TrackOutOfStock.contextType = ExperienceContext;

TrackOutOfStock.contextTypes = {
  path: string,
};

TrackOutOfStock.propTypes = {
  /* eslint-disable-next-line react/no-unused-prop-types */
  internally: bool,
  itemId: string
};

TrackOutOfStock.defaultProps = {
  internally: false,
  itemId: null
};
export default TrackOutOfStock;
